/**
 * Skill Provider — on-demand keyed document collections.
 *
 * Extends ContextProvider with `load()` for on-demand content fetching
 * and a keyed `set()` for writing individual entries.
 *
 * Duck-typed: if a provider has a `load` method, it's a SkillProvider.
 */

import type { ContextProvider } from "./context";

/**
 * Storage interface for skill collections.
 *
 * - `get()` returns metadata listing (rendered into system prompt)
 * - `load(key)` fetches full content (via load_context tool)
 * - `set(key, content, description?)` writes an entry (via set_context tool)
 */
export interface SkillProvider extends ContextProvider {
  load(key: string): Promise<string | null>;
  set?(key: string, content: string, description?: string): Promise<void>;
}

/**
 * Check if a provider is a SkillProvider (has a `load` method).
 */
export function isSkillProvider(provider: unknown): provider is SkillProvider {
  return (
    typeof provider === "object" &&
    provider !== null &&
    "load" in provider &&
    typeof (provider as SkillProvider).load === "function"
  );
}

// ── R2 Skill Provider ──────────────────────────────────────────────

/**
 * SkillProvider backed by an R2 bucket.
 *
 * - `get()` returns a metadata listing of all skills (key + description)
 * - `load(key)` fetches a skill's full content
 * - `set(key, content, description?)` writes a skill
 *
 * Descriptions are pulled from R2 custom metadata (`description` key).
 * If a prefix is provided, it is prepended on storage operations and
 * stripped from keys in metadata. `keys`, when provided, is matched against
 * these prefix-relative keys.
 *
 * @example
 * ```ts
 * const skills = new R2SkillProvider(env.SKILLS_BUCKET, {
 *   prefix: "skills/",
 *   keys: ["code-review", "debugging"]
 * });
 * ```
 */
export class R2SkillProvider implements SkillProvider {
  private bucket: R2Bucket;
  private prefix: string;
  private keys: Set<string> | null;

  constructor(
    bucket: R2Bucket,
    options?: { prefix?: string; keys?: string[] }
  ) {
    this.bucket = bucket;
    this.prefix = options?.prefix ?? "";
    this.keys = options?.keys?.length ? new Set(options.keys) : null;
  }

  async get(): Promise<string | null> {
    const entries: string[] = [];
    let cursor: string | undefined;
    let truncated = true;
    while (truncated) {
      const listed = await this.bucket.list({
        prefix: this.prefix,
        cursor,
        include: ["customMetadata"]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      for (const obj of listed.objects) {
        const key = obj.key.slice(this.prefix.length);
        if (!this.allowsKey(key)) continue;
        const desc = obj.customMetadata?.description;
        entries.push(`- ${key}${desc ? `: ${desc}` : ""}`);
      }
      truncated = listed.truncated;
      cursor = listed.truncated ? listed.cursor : undefined;
    }
    return entries.length > 0 ? entries.join("\n") : null;
  }

  async load(key: string): Promise<string | null> {
    if (!this.allowsKey(key)) return null;
    const obj = await this.bucket.get(this.prefix + key);
    if (!obj) return null;
    return obj.text();
  }

  async set(key: string, content: string, description?: string): Promise<void> {
    await this.bucket.put(this.prefix + key, content, {
      customMetadata: description ? { description } : undefined
    });
  }

  private allowsKey(key: string): boolean {
    return this.keys === null || this.keys.has(key);
  }
}
